// src/oura/components/onboarding/UseAnotherScan.tsx
//
// "Use another scan" on the Steps card's First AI check (#4773, backend #6338):
// staff name a completed scan of this business as its First AI check. The list
// is read only when it is asked for, the confirm sits in place (no browser
// dialog), and every refusal says why in one sentence. A refusal changes nothing.

import { useState } from "react";
import { cn } from "../../lib/cn";
import { FOCUS_RING, PillButton, TYPE } from "@oura/ui";
import { useSnackbar } from "@hook/use-snackbar";
import { chooseFirstCheck, FIRST_CHECK_SCAN_LIMIT, listFirstCheckScans, type Onboarding } from "@service/aeo/onboarding";
import type { AeoAssessment } from "@service/aeo/types";
import { useOuraRole } from "../../role";

/** A plain sentence per refusal code (backend #6338). The server's own message is the fallback. */
const REFUSAL: Record<string, string> = {
    invalid_request: "That is not a scan we can use. Pick another one and try again.",
    staff_only: "Only ourteam staff can change the First AI check.",
    not_this_business: "That scan belongs to another business, so it cannot be the First AI check here. The list has been read again.",
    scan_not_complete: "That scan has not finished, so it cannot be the First AI check. The list has been read again.",
    scan_running: "The First AI check is running now, so it cannot be changed. Try again once it has finished.",
    results_already_sent: "The results were already sent to the client, so the First AI check is locked.",
};

/** The short day in Malaysia time, the way the Steps card shows a day: "28 Oct". */
function shortDay(iso: string | null | undefined): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "Asia/Kuala_Lumpur" });
}

/** The scans this business can use: complete, and under the same base_path (the server refuses any other). */
function completedScansFor(rows: AeoAssessment[], basePath: string): AeoAssessment[] {
    return rows.filter((s) => s.status === "complete" && (s.base_path ?? "") === basePath);
}

/** The visibility count a scan carries, as the business detail reads it (`results.aiVisibility`). */
type ScanVisibility = { aiVisibility?: { questionsNamed?: number; questionsOf?: number } };

/** "Named in 21 of 50", when the scan carries its visibility count. Null otherwise: never a guessed number. */
function namedLine(scan: AeoAssessment): string | null {
    const vis = (scan.results as unknown as ScanVisibility | undefined)?.aiVisibility;
    const named = vis?.questionsNamed;
    const of = vis?.questionsOf;
    if (typeof named !== "number" || typeof of !== "number" || of <= 0) return null;
    return `Named in ${named} of ${of}`;
}

export function UseAnotherScan({ businessId, domain, basePath, onboarding, onPicked, onRefresh }: {
    businessId: string;
    domain: string;
    basePath: string;
    onboarding: Onboarding;
    /** The record the server answered with: the page applies it and re-reads the plan. */
    onPicked: (next: Onboarding) => void;
    onRefresh: () => void;
}) {
    const snackbar = useSnackbar();
    // Staff only, as the route is: a merchant, or a view-as session, never sees it.
    const isStaff = useOuraRole() === "oura-staff";
    const [open, setOpen] = useState(false);
    const [scans, setScans] = useState<AeoAssessment[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [listFailed, setListFailed] = useState<string | null>(null);
    /** The list hit the server's cap, so an older scan of this domain may be missing from it. */
    const [capped, setCapped] = useState(false);
    /** The scan whose confirm sentence is open. One at a time. */
    const [picking, setPicking] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [refusal, setRefusal] = useState<string | null>(null);

    const currentId = onboarding.scan.assessmentId ?? null;
    const setBy = onboarding.firstCheckSetBy ?? null;
    // Hidden once results were sent (409 results_already_sent), and while a scan runs (409 scan_running).
    if (!isStaff || onboarding.resultsSentAt != null || onboarding.scan.status === "running") return null;

    const load = async () => {
        setLoading(true);
        setListFailed(null);
        const r = await listFirstCheckScans(businessId, domain, basePath);
        setLoading(false);
        if (!r.ok) { setScans(null); setCapped(false); setListFailed(r.message); return; }
        const mine = completedScansFor(r.scans, basePath);
        // Capped only when the limit's worth of this site's scans came back.
        setCapped(mine.length >= FIRST_CHECK_SCAN_LIMIT);
        setScans(mine);
    };

    const show = () => {
        setOpen(true);
        setRefusal(null);
        setPicking(null);
        void load();
    };

    const hide = () => {
        setOpen(false);
        setPicking(null);
        setRefusal(null);
    };

    const confirm = async (assessmentId: string) => {
        setBusy(true);
        setRefusal(null);
        const r = await chooseFirstCheck(businessId, assessmentId);
        setBusy(false);
        if (!r.ok) {
            setPicking(null);
            const sentence = REFUSAL[r.code ?? ""] ?? r.message;
            // The check is running or the results are out: the action goes, so the page is read again.
            // The picker closes here, so the sentence goes in a snackbar, which outlives the close.
            if (r.code === "scan_running" || r.code === "results_already_sent") {
                snackbar.showError("The First AI check was not changed", sentence);
                setRefusal(null);
                setOpen(false);
                onRefresh();
            } else {
                setRefusal(sentence);
                void load();
            }
            return;
        }
        setOpen(false);
        setPicking(null);
        onPicked(r.onboarding);
        snackbar.showSuccess("The First AI check is this scan now", "Approval and the Baseline Snapshot start again from it. Nothing is emailed.");
    };

    const link = "rounded font-medium text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-800";

    return (
        <div className="mt-3 border-t border-gray-100 pt-3" data-use-another-scan="">
            <div className="flex flex-wrap items-center justify-between gap-2">
                {setBy ? (
                    <p className={cn(TYPE.tc, "m-0 text-gray-500")}>Set by {setBy.name}, {shortDay(setBy.at)}</p>
                ) : <span />}
                {!open && (
                    <button type="button" onClick={show} className={cn(TYPE.tc, link, FOCUS_RING)}>
                        Use another scan
                    </button>
                )}
            </div>

            {open && (
                <div className="mt-3 flex flex-col gap-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className={cn(TYPE.tb, "m-0 text-gray-900")}>Completed scans</p>
                        <button type="button" onClick={hide} disabled={busy} className={cn(TYPE.tc, link, FOCUS_RING, "disabled:opacity-50")}>
                            Hide
                        </button>
                    </div>

                    {loading && <p className={cn(TYPE.tc, "m-0 text-gray-500")}>Reading this business&rsquo;s completed scans&hellip;</p>}
                    {!loading && listFailed && <p role="alert" className={cn(TYPE.tc, "m-0 font-medium text-rose-600")}>{listFailed}</p>}
                    {!loading && !listFailed && scans && scans.length === 0 && (
                        <p className={cn(TYPE.tc, "m-0 text-gray-500")}>No completed scans of this business yet.</p>
                    )}

                    {!loading && scans && scans.length > 0 && (
                        <ul className="m-0 flex list-none flex-col divide-y divide-gray-100 p-0">
                            {scans.map((s) => {
                                const isCurrent = s.assessment_id === currentId;
                                const named = namedLine(s);
                                const day = shortDay(s.completed_at ?? s.created_at);
                                return (
                                    <li key={s.assessment_id} className="flex flex-col gap-2 py-2.5 sm:flex-row sm:items-center sm:justify-between">
                                        <div className="flex min-w-0 flex-col">
                                            <span className={cn(TYPE.tb, "text-gray-900")}>{day}</span>
                                            {named && <span className={cn(TYPE.tc, "text-gray-500")}>{named}</span>}
                                        </div>
                                        {isCurrent ? (
                                            <span className={cn(TYPE.tc, "font-medium text-gray-700")}>The First AI check</span>
                                        ) : (
                                            <PillButton variant="text" size="sm" disabled={busy} onClick={() => { setRefusal(null); setPicking(s.assessment_id); }}>
                                                Use this scan
                                            </PillButton>
                                        )}
                                        {picking === s.assessment_id && (
                                            <div className="w-full basis-full rounded-md bg-gray-50 p-3">
                                                <p className={cn(TYPE.tc, "m-0 text-gray-700")}>
                                                    Approval and the Baseline Snapshot start again from this scan. Nothing is emailed.
                                                </p>
                                                <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                                                    <PillButton variant="text" size="sm" disabled={busy} onClick={() => setPicking(null)}>Cancel</PillButton>
                                                    <PillButton variant="primary" size="sm" busy={busy} onClick={() => void confirm(s.assessment_id)}>
                                                        Set as the First AI check
                                                    </PillButton>
                                                </div>
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {capped && !loading && (
                        <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                            Only the newest {FIRST_CHECK_SCAN_LIMIT} scans are listed, so an older one may not appear here.
                        </p>
                    )}

                    {refusal && <p role="alert" className={cn(TYPE.tc, "m-0 font-medium text-rose-600")}>{refusal}</p>}
                </div>
            )}
        </div>
    );
}
